import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { MouseEvent, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { RESET_HEALTH_STATUS_ONCLOSE_DELAY } from '../../constants';
import { CheckStatus } from '../../redux/features/healthStatusCheck/healthStatusCheck.constant';
import { ISystemsStatus } from '../../redux/features/healthStatusCheck/healthStatusCheck.props';
import { selectHealthStatusCheckState } from '../../redux/features/healthStatusCheck/healthStatusCheck.selector';
import {
  checkHealthStatus,
  resetHealthStatusState,
} from '../../redux/features/healthStatusCheck/healthStatusCheck.slice';
import { SessionStatus } from '../../redux/features/sessionBoundary/sessionBoundary.constants';
import { selectSessionStatus } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import {
  selectIsStaffIntervention,
  selectIsWebSocketConnected,
} from '../../selectors/message';
import { selectActiveRestaurantCode } from '../../selectors/restaurant';
import Colors from '../../utils/color';
import {
  HITL7_HEADER_ICON_BOX,
  HITL7_HEADER_ICON_INSET,
} from '../../utils/hitl3Spacing';
import {
  HealthStatusCheckBody,
  SendReportStatus,
  useHealthCheckOnPosmonDisconnect,
  useHealthStatusReport,
} from '../VisualIndicator/HealthStatusCheckIcon';

export const ALL_SYSTEMS_OK = 'All systems OK';

// "UNHEALTHY" → "Unhealthy": the diagnostic API's raw status, in the panel's case.
const toTitleCase = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
export const NOTHING_TO_MONITOR = 'No restaurant selected';

/**
 * One row of the System panel. `tone` drives the icon, the tooltip and the
 * value's colour: a fault is red, normal and unknown are plain — nothing in
 * the panel is green, so it never vouches for something it cannot see.
 */
export type SystemRowTone = 'fault' | 'normal' | 'unknown';
export interface SystemRow {
  label: string;
  value: string;
  tone: SystemRowTone;
}

export interface SystemStatus {
  /** Plain-language name of every fault, in a fixed order; empty when fine. */
  faults: string[];
  rows: SystemRow[];
  isChecking: boolean;
  /** False before a restaurant is chosen — there is nothing to monitor yet. */
  isMonitoring: boolean;
}

/**
 * The four signals the System icon folds, each reduced to a panel row and,
 * where it is wrong, a fault.
 *
 *   signal               fault when                  unknown when
 *   ───────────────────  ──────────────────────────  ─────────────────────────────
 *   network              socket down                 no restaurant chosen yet —
 *                                                    nothing to be connected to
 *   TTS                  off DURING a session        no session — isTTSOn starts
 *                                                    false and only a tts_on event
 *                                                    (requested at session start)
 *                                                    sets it, so between cars it
 *                                                    says nothing
 *   health check         a completed check found a   no check has run (not a
 *                        system unhealthy            failure); spinner while it runs
 *   staff intervention   staff have taken the order  — (Yes / No; clears with the car)
 *
 * Reporting an unknown as fine is how the first cut showed "Connected" and
 * "On" with no restaurant selected. Counting it as a fault would hold the
 * icon amber between every car, and an alarm that is always on is one nobody
 * looks at — so it is neither.
 */
export const useSystemStatus = (): SystemStatus => {
  const isConnected = useAppSelector(selectIsWebSocketConnected);
  const isTTSOn = useAppSelector((state) => state.messages.isTTSOn);
  const isStaffIntervention = Boolean(
    useAppSelector(selectIsStaffIntervention)
  );
  const sessionStatus = useAppSelector(selectSessionStatus);
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const { isHealthy, checkStatus, healthStatusCheckContent } = useAppSelector(
    selectHealthStatusCheckState
  );

  const isMonitoring = Boolean(restaurantCode);
  const isInSession = sessionStatus === SessionStatus.start;
  const isChecking = checkStatus === CheckStatus.pending;
  const isHealthDone = checkStatus === CheckStatus.completed;

  const network: SystemRow = !isMonitoring
    ? { label: 'Network', value: 'No restaurant selected', tone: 'unknown' }
    : isConnected
    ? { label: 'Network', value: 'Connected', tone: 'normal' }
    : { label: 'Network', value: 'Disconnected', tone: 'fault' };
  const tts: SystemRow = !isInSession
    ? { label: 'TTS', value: 'Waiting for a car', tone: 'unknown' }
    : isTTSOn
    ? { label: 'TTS', value: 'On', tone: 'normal' }
    : { label: 'TTS', value: 'Off', tone: 'fault' };
  const staff: SystemRow = isStaffIntervention
    ? { label: 'Staff intervention', value: 'Yes', tone: 'fault' }
    : { label: 'Staff intervention', value: 'No', tone: 'normal' };
  const health: SystemRow = isChecking
    ? { label: 'Health check', value: 'Checking…', tone: 'unknown' }
    : isHealthDone && !isHealthy
    ? { label: 'Health check', value: 'Issues found', tone: 'fault' }
    : isHealthDone
    ? { label: 'Health check', value: 'Healthy', tone: 'normal' }
    : { label: 'Health check', value: 'Not checked', tone: 'unknown' };

  // Each system the check flagged, as an ordinary row under Health check —
  // "Posmon Service · Unhealthy", in the panel's own style rather than the
  // legacy popover's bold red capitals.
  const { body } = healthStatusCheckContent;
  const unhealthySystems: SystemRow[] =
    health.tone === 'fault' && Array.isArray(body)
      ? (body as ISystemsStatus[]).map(({ name, status }) => ({
          label: name,
          value: toTitleCase(status),
          tone: 'fault',
        }))
      : [];

  const faults = [
    network.tone === 'fault' && 'Network disconnected',
    tts.tone === 'fault' && 'TTS off',
    health.tone === 'fault' && 'Health check failed',
    staff.tone === 'fault' && 'Staff intervention',
  ].filter((fault): fault is string => Boolean(fault));

  return {
    faults,
    rows: [network, tts, staff, health, ...unhealthySystems],
    isChecking,
    isMonitoring,
  };
};

const ICON_SX = {
  width: HITL7_HEADER_ICON_BOX,
  height: HITL7_HEADER_ICON_BOX,
  p: `${HITL7_HEADER_ICON_INSET}px`,
} as const;

const StatusRow = ({ label, value, tone }: SystemRow) => (
  <Box
    sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
    data-testid={`system-row-${label.toLowerCase().replace(/\s+/g, '-')}`}
    data-tone={tone}
  >
    {/* No dot and no bold: a fault's value is red, every other value plain,
        so the panel reads one way throughout. */}
    <Typography variant="body2" sx={{ flex: 1 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        textAlign: 'right',
        // Red marks an error state — the theme's error.main, the red the
        // Cancel Order button fills with. Weight and case stay as every other
        // value, so the colour is the only difference.
        color: tone === 'fault' ? 'error.main' : 'text.primary',
      }}
    >
      {value}
    </Typography>
  </Box>
);

/**
 * HITL-1702 — the 7.0 System icon.
 *
 * Folds four header indicators (health check, TTS mic, network, staff headset)
 * into one, because supervisors reported the 7.0 header as too crowded to find
 * Exit and the speaker. Two colours only: green when everything is fine, amber
 * when anything is wrong — there is no red here, and several faults at once are
 * still just amber. The tooltip names every fault, so the agent does not have to
 * open anything to know what; the panel is for detail and for the health
 * check's Report / Copy actions.
 *
 * One click opens the panel AND starts a health check when none has run, so the
 * agent keeps today's single action (legacy: the first click only runs the
 * check, and the popover opens on a later click and only on a failure).
 *
 * Replaces HealthStatusCheckIcon in the header under 7.0 and takes over its
 * automatic check on a POS-monitor disconnect; exactly one of the two is ever
 * mounted. New component, so `sx` only — no makeStyles sheet attaches with the
 * flag off.
 */
const SystemStatusIndicator = () => {
  const dispatch = useAppDispatch();
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const { checkStatus } = useAppSelector(selectHealthStatusCheckState);
  const { faults, rows, isChecking, isMonitoring } = useSystemStatus();
  const { sendReportStatus, setSendReportStatus, onReportClick } =
    useHealthStatusReport();
  const { generatedTime } = useAppSelector(selectHealthStatusCheckState);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  useHealthCheckOnPosmonDisconnect();

  const hasFault = faults.length > 0;
  const tooltip = hasFault
    ? faults.join(' · ')
    : isMonitoring
    ? ALL_SYSTEMS_OK
    : NOTHING_TO_MONITOR;

  const runHealthCheck = () => {
    if (restaurantCode && !checkStatus) dispatch(checkHealthStatus());
  };

  // Copies the whole panel, not just the health result: the title, every row,
  // then the systems the check flagged and when it ran — what a supervisor
  // needs pasted into a thread in one go.
  const onCopyPanelClick = () => {
    const text = [
      restaurantCode || 'System status',
      ...rows.map(({ label, value }) => `${label}: ${value}`),
      generatedTime,
    ]
      .filter(Boolean)
      .join('\n');
    navigator.clipboard.writeText(text);
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    runHealthCheck();
  };

  // Same reset as the legacy popover: closing clears a failed result (the
  // agent has seen it) after a short delay so the panel does not blank mid-fade.
  const handleClose = () => {
    setAnchorEl(null);
    setTimeout(() => {
      dispatch(resetHealthStatusState());
      setSendReportStatus(SendReportStatus.notAplicable);
    }, RESET_HEALTH_STATUS_ONCLOSE_DELAY);
  };

  return (
    <>
      <Tooltip title={tooltip}>
        <IconButton
          data-testid="system-status"
          data-fault={hasFault}
          aria-label={`System status: ${tooltip}`}
          onClick={handleClick}
          sx={{ ...ICON_SX, flexShrink: 0 }}
        >
          {isChecking ? (
            <CircularProgress
              data-testid="system-status-checking"
              size={HITL7_HEADER_ICON_BOX - 2 * HITL7_HEADER_ICON_INSET}
              sx={{ color: Colors.white }}
            />
          ) : hasFault ? (
            <ReportProblemRoundedIcon
              sx={{ fontSize: 18, color: Colors.acknowledge }}
            />
          ) : (
            // Grey, not green, before a restaurant is chosen: there is nothing
            // being watched, so "all OK" would be a claim it cannot make.
            <CheckCircleRoundedIcon
              sx={{
                fontSize: 18,
                color: isMonitoring ? Colors.brightGreen : Colors.dim,
              }}
            />
          )}
        </IconButton>
      </Tooltip>
      <Popover
        id={anchorEl ? 'system-status-popover' : undefined}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // Portals out of the header, so tag it as header chrome — otherwise a
        // click in it counts as an order interaction (see Player).
        PaperProps={{ 'data-header': 'true' } as Record<string, string>}
      >
        <Box
          sx={{ minWidth: 300, maxWidth: 380 }}
          data-testid="system-status-panel"
        >
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            {/* The restaurant titles the panel. Codes are long with no spaces,
                so it may break after any underscore — and only there, never
                mid-word. */}
            <Typography
              variant="body2"
              color="primary"
              data-testid="system-status-title"
              sx={{ fontWeight: 700, overflowWrap: 'break-word' }}
            >
              {restaurantCode
                ? restaurantCode.split('_').map((part, i, parts) => (
                    <React.Fragment key={i}>
                      {part}
                      {i < parts.length - 1 && (
                        <>
                          _<wbr />
                        </>
                      )}
                    </React.Fragment>
                  ))
                : 'System status'}
            </Typography>
          </Box>
          {/* The one rule in the panel: it sets the title off from the rows. */}
          <Divider data-testid="system-status-title-divider" />
          <Box sx={{ px: 3, pt: 1, pb: 1.5 }}>
            {rows.map((row) => (
              <StatusRow key={row.label} {...row} />
            ))}
          </Box>
          {/* Health detail sits under its row, only when there is something to
              show or do: the failed systems + Report / Copy, or a way to run
              the check again (a healthy result clears itself after 5s). */}
          {checkStatus === CheckStatus.completed ? (
            <HealthStatusCheckBody
              embedded
              onClose={handleClose}
              sendReportStatus={sendReportStatus}
              onReportClick={onReportClick}
              onCopyClick={onCopyPanelClick}
            />
          ) : (
            !isChecking &&
            restaurantCode && (
              <Box sx={{ px: 3, pb: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={runHealthCheck}
                >
                  Run health check
                </Button>
              </Box>
            )
          )}
        </Box>
      </Popover>
    </>
  );
};

export default SystemStatusIndicator;
